import { Injectable, inject, signal, computed, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { OAuthService } from 'angular-oauth2-oidc';
import { filter, take } from 'rxjs';
import { ApiMeDto, UsersService } from '@vasta/api-vasta';
import { ImpersonationService } from '../impersonation.service';

@Injectable({ providedIn: 'root' })
export class AuthFacade {
  private readonly oauth = inject(OAuthService);
  private readonly usersService = inject(UsersService);
  private readonly injector = inject(Injector);
  private readonly impersonation = inject(ImpersonationService);

  private readonly _user = signal<ApiMeDto | null>(null);
  private readonly _loading = signal(false);

  readonly tenants = computed(() => this._user()?.tenants || []);
  readonly isLoggedIn = computed(() => this.oauth.hasValidAccessToken());
  readonly currentUser = this._user.asReadonly();
  readonly loading = this._loading.asReadonly();

  /**
   * Who is behind the session when this tab is viewing the application as
   * somebody else — null when it is the caller's own. Read from the API's
   * answer rather than from the tab's own state, so the banner appears only
   * once the hand-off has actually been honoured.
   */
  readonly impersonatedBy = computed(
    () => this._user()?.impersonatedBy ?? null,
  );

  /**
   * Emits once when the current-user load settles (success or error).
   * Each subscription waits for the next settled state, so guards can
   * rely on `tenants()` being up to date.
   */
  readonly whenUserSettled$ = toObservable(this.loading, {
    injector: this.injector,
  }).pipe(
    filter((loading) => !loading),
    take(1),
  );

  readonly displayName = computed(() => {
    const u = this._user();
    if (!u) return '';
    return `${u.firstName || ''} ${u.lastName || ''}`.trim();
  });

  readonly initials = computed(() => {
    const u = this._user();
    if (!u) return '';
    if (!u.firstName || !u.lastName) return 'XX';
    return `${u.firstName.charAt(0)}${u.lastName.charAt(0)}`.toUpperCase();
  });

  loadCurrentUser(): void {
    if (!this.oauth.hasValidAccessToken()) return;
    this._loading.set(true);
    this.usersService.getMeUsers().subscribe({
      next: (user) => {
        this._user.set(user);
        this.impersonation.reconcile(user);
        this._loading.set(false);
      },
      error: () => this._loading.set(false),
    });
  }

  login(): void {
    this.oauth.initLoginFlow();
  }

  logout(): void {
    this._user.set(null);
    this.oauth.logOut();
  }
}
